import React, {
    useEffect,
    useState,
    forwardRef,
    useImperativeHandle,
    useRef,
} from "react";
import {
    ArrowUpDown,
    ArrowUpNarrowWide,
    ArrowDownWideNarrow,
} from "lucide-react";
import { toast } from "react-toastify";
import ReactPaginate from "react-paginate";
import { useApiQuery } from "../../hooks/useApiQuery";
import { useQueryEffect } from "../../hooks/useQueryEffect";
import {
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import "./TableWithPagination.scss";
const defaultLimitOptions = [10, 25, 50, 100, 200, 500, 1000];
const selectCss = {
    color: "#000",
    border: "0",
    // minWidth: 200,
    height: "40px",
    "& .MuiOutlinedInput-notchedOutline": {
        border: "1px solid #F0F0F0 !important",
        borderRadius: "10px",
    },
    ":hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#F0F0F0",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: "1px solid #F0F0F0 !important",
    },
};

const ReusableTable = forwardRef(
    (
        {
            routeType = "default",
            headers = [],
            apiPagination = false,
            fetchData = () => { },
            data: propData, // allow direct data prop for non-apiPagination
            defaultLimit = 10,
            limitOptions = defaultLimitOptions,
            selectable = false,
            customCellRender = {},
            onDataLoaded,
            pdfPrint,
        },
        ref
    ) => {
        const [limit, setLimit] = useState(defaultLimit);
        const [page, setPage] = useState(1);
        const [search, setSearch] = useState("");
        const [sort, setSort] = useState({ key: null, direction: null });
        const [selectedRows, setSelectedRows] = useState([]);
        const [localData, setLocalData] = useState([]);
        const [total, setTotal] = useState(0);
        const [dateRange, setDateRange] = useState([null, null]);
        const [selectedHelpStatus, setSelectedHelpStatus] = useState("all");

        const tableRef = useRef(null);
        const [startDate, endDate] = dateRange;

        // API data fetch
        const { data, isLoading, isError, error, refetch } = useApiQuery({
            queryKey: [
                "table-data",
                routeType,
                {
                    page,
                    limit,
                    search,
                    sort,
                    startDate,
                    endDate,
                    status: selectedHelpStatus,
                },
            ],
            queryFn: () =>
                fetchData({
                    page,
                    limit: limit === "All" ? 1000000 : limit, // large number for "All"
                    search,
                    sort,
                    startDate,
                    endDate,
                    status: selectedHelpStatus,
                }),
            enabled: apiPagination,
            refetchOnMount: true,
        });

        // Handle API data
        useQueryEffect({
            data,
            isError,
            error,
            onSuccess: (data) => {
                let rows = Array.isArray(data.data) ? data.data : [];
                // Ensure each row has a unique id
                rows = rows.map((row, idx) => ({
                    ...row,
                    id: row.id ?? row.sNo ?? idx,
                }));
                setLocalData(rows);
                setTotal(data.total ?? rows.length);
                if (onDataLoaded) {
                    onDataLoaded(data);
                }
            },
            onError: (err) => {
                const msg =
                    err?.response?.data?.meta?.message ||
                    err?.response?.data?.message ||
                    err?.message ||
                    "Something went wrong";
                toast.error(msg);
            },
        });

        // Handle local data (non-apiPagination)
        // ...existing code...
        useEffect(() => {
            if (!apiPagination && Array.isArray(propData)) {
                let filtered = [...propData];
                // Ensure each row has a unique id
                filtered = filtered.map((row, idx) => ({
                    ...row,
                    id: row.id ?? row.sNo ?? idx,
                }));
                if (search) {
                    filtered = filtered.filter((item) =>
                        Object.values(item).some((val) =>
                            String(val).toLowerCase().includes(search.toLowerCase())
                        )
                    );
                }
                if (sort.key) {
                    filtered.sort((a, b) => {
                        // Add your sorting logic here if needed
                        if (a[sort.key] < b[sort.key])
                            return sort.direction === "asc" ? -1 : 1;
                        if (a[sort.key] > b[sort.key])
                            return sort.direction === "asc" ? 1 : -1;
                        return 0;
                    });
                }
                setTotal(filtered.length);
                let paginated;
                if (pdfPrint) {
                    paginated = filtered;
                } else if (limit === "All") {
                    paginated = filtered;
                } else {
                    const start = (page - 1) * limit;
                    paginated = filtered.slice(start, start + limit);
                }
                setLocalData(paginated);
            }
        }, [propData, page, limit, search, sort, apiPagination, pdfPrint]);
        // ...existing code...

        useImperativeHandle(ref, () => ({
            refetchTable: () => {
                refetch();
            },
        }));

        const totalPages =
            limit === "All"
                ? 1
                : Math.ceil(total / (limit === "All" ? total : limit));

        const handleLimitChange = (event) => {
            const value = event.target.value;
            setLimit(value === "All" ? "All" : Number(value));
            setPage(1);
        };

        const handlePageChange = ({ selected }) => {
            setPage(selected + 1);
        };

        const handleSort = (key) => {
            setSort((prev) => {
                if (prev.key !== key) return { key, direction: "asc" };
                if (prev.direction === "asc") return { key, direction: "desc" };
                return { key: null, direction: null };
            });
            setPage(1);
        };

        const getSortIcon = (key) => {
            if (sort.key !== key) return <ArrowUpDown className="ml-1 " size={15} />;
            return sort.direction === "asc" ? (
                <ArrowUpNarrowWide className="ml-1 text-[#FF8800]" size={15} />
            ) : (
                <ArrowDownWideNarrow className="ml-1 text-[#FF8800]" size={15} />
            );
        };

        return (
            <div
                className="p-7.5 shadow-sm bg_box_color_and_border max-h-[80svh] overflow-auto"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
                {selectedRows?.length > 0 && (
                    <span className="text-sm text-gray-200">
                        {selectedRows.length} row(s) selected
                    </span>
                )}

                <div
                    className="overflow-x-auto rounded-lg bg_box_color_and_border max-h-[620px] overflow-auto  "
                    ref={tableRef}
                >
                    <TableContainer className="noscroll">
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <TableRow
                                    sx={{
                                        backgroundColor: "#F8F8F8",
                                        position: "sticky",
                                        top: "0",
                                        zIndex: "1",
                                    }}
                                >
                                    {headers.map((header) => (
                                        <TableCell
                                            key={header.key}
                                            sx={{
                                                backgroundColor: "transparent",
                                                padding: "10px",
                                                fontWeight: "600",
                                                fontSize: "15px",
                                                cursor: "pointer",
                                                color: "#2B2B2B",
                                            }}
                                            onClick={() => {
                                                if (header.filterable) handleSort(header.key);
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                }}
                                            >
                                                {header.label}
                                                {header.filterable && getSortIcon(header.key)}
                                            </div>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {localData.length === 0 && !isLoading && (
                                    <TableRow>
                                        <TableCell
                                            colSpan={headers.length + (selectable ? 1 : 0)}
                                            sx={{
                                                textAlign: "center",
                                                color: "#2B2B2B",
                                                padding: "10px",
                                                fontSize: "14px",
                                            }}
                                        >
                                            No data found
                                        </TableCell>
                                    </TableRow>
                                )}
                                {isLoading && (
                                    <TableRow>
                                        <TableCell
                                            colSpan={headers.length + (selectable ? 1 : 0)}
                                            sx={{
                                                textAlign: "center",
                                                padding: "10px",
                                                fontSize: "14px",
                                                textTransform: "lowercase",
                                            }}
                                        >
                                            Loading...
                                        </TableCell>
                                    </TableRow>
                                )}
                                {!isLoading &&
                                    localData.map((row) => (
                                        <TableRow
                                            key={row.id}
                                            sx={{
                                                cursor: "pointer",
                                                ":hover": {
                                                    backgroundColor: "#000",
                                                },
                                            }}
                                        >
                                            {headers.map((header) => (
                                                <TableCell
                                                    key={header.key}
                                                    sx={{
                                                        padding: "10px",
                                                        fontSize: "14px",
                                                        textTransform: "lowercase",
                                                    }}
                                                >
                                                    {header.render
                                                        ? header.render(row)
                                                        : customCellRender[header.key]
                                                            ? customCellRender[header.key](row[header.key], row)
                                                            : row[header.key]}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
                {!pdfPrint && (
                    <div className="tablePaginationMainBox">
                        <div className="paginationInnerBox">
                            <p
                                style={{
                                    margin: "0",
                                }}
                            >
                                show
                            </p>

                            <Select
                                value={limit.toString()}
                                onChange={handleLimitChange}
                                sx={selectCss}
                            >
                                <MenuItem value="All">All</MenuItem>
                                {limitOptions.map((opt) => (
                                    <MenuItem key={opt} value={opt.toString()}>
                                        {opt}
                                    </MenuItem>
                                ))}
                            </Select>

                            <p
                                style={{
                                    margin: "0",
                                }}
                            >
                                entries
                            </p>
                        </div>

                        {totalPages > 1 && (
                            <ReactPaginate
                                previousLabel={
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        class="lucide lucide-chevron-left-icon lucide-chevron-left"
                                    >
                                        <path d="m15 18-6-6 6-6" />
                                    </svg>
                                }
                                nextLabel={
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        class="lucide lucide-chevron-right-icon lucide-chevron-right"
                                    >
                                        <path d="m9 18 6-6-6-6" />
                                    </svg>
                                }
                                pageCount={totalPages}
                                onPageChange={handlePageChange}
                                containerClassName="paginate-container"
                                pageLinkClassName="paginate-page"
                                activeLinkClassName="paginate-active"
                                previousLinkClassName="paginate-previous"
                                nextLinkClassName="paginate-next"
                                disabledClassName="paginate-disabled"
                            />
                        )}
                    </div>
                )}
            </div>
        );
    }
);

ReusableTable.displayName = "ReusableTable";
export default ReusableTable;